import type { ReactNode } from 'react'

import { Checkbox, Col, Radio, Skeleton } from 'antd'
import { useFormikContext } from 'formik'

import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { Input } from 'common/components/Input/Input'
import { InputNumber } from 'common/components/InputNumber/InputNumber'
import { Label } from 'common/components/Label/Label'
import { Select } from 'common/components/Select/Select'

import type {
  DynamicFieldValue,
  DynamicFieldValues,
  EmrCodeDynamicField,
} from 'features/Procedure/state/procedure-api.type'

import styles from './dynamic-fields.module.scss'
import { getBindingOptions, isRenderableField, toIntegerValue } from './dynamic-fields.utils'
import type { FormValues } from './schema'

type Props = {
  disabled?: boolean
}

const SKELETON_PLACEHOLDER_KEYS = ['first', 'second', 'third']

export const DynamicFieldsSkeleton = () => (
  <>
    {SKELETON_PLACEHOLDER_KEYS.map((key) => (
      <Col xs={8} key={key}>
        <Skeleton active title={false} paragraph={{ rows: 2, width: ['40%', '100%'] }} />
      </Col>
    ))}
  </>
)

export const DynamicFields = ({ disabled = false }: Props) => {
  const { values, errors, setFieldValue } = useFormikContext<FormValues>()

  const allFields: EmrCodeDynamicField[] = values.dynamic_fields ?? []
  const fields = allFields.filter(isRenderableField)
  const fieldValues: DynamicFieldValues = values.dynamic_field_values ?? {}
  const fieldErrors = (errors.dynamic_field_values ?? {}) as Record<string, string | undefined>

  if (!fields.length) {
    return null
  }

  const handleValueChange = (name: string, newValue: DynamicFieldValue) => {
    setFieldValue('dynamic_field_values', { ...fieldValues, [name]: newValue })
  }

  const renderControl = (field: EmrCodeDynamicField): ReactNode => {
    const value = fieldValues[field.name]
    const error = fieldErrors[field.name]

    switch (field.type) {
      case 'integer':
        return (
          <>
            <Label label={field.label} required={field.required} />
            <InputNumber
              value={value as number | undefined}
              name={field.name}
              precision={0}
              formatter={(formatValue) => String(formatValue ?? '').replace(/\D/g, '')}
              parser={(text) => (text ?? '').replace(/\D/g, '')}
              onChange={(newValue) =>
                handleValueChange(
                  field.name,
                  newValue === null || newValue === undefined
                    ? undefined
                    : toIntegerValue(newValue),
                )
              }
              placeholder={field.label}
              disabled={disabled}
              status={error ? 'error' : ''}
            />
            <ErrorLabel error={error} />
          </>
        )
      case 'dropdown':
        return (
          <Select
            label={field.label}
            required={field.required}
            value={(value as string | undefined) ?? undefined}
            listOptions={getBindingOptions(field.bindings)}
            onChange={(newValue: string) => handleValueChange(field.name, newValue)}
            placeholder={field.label}
            disabled={disabled}
            error={error}
            allowClear
          />
        )
      case 'multiselect':
        return (
          <Select
            label={field.label}
            required={field.required}
            mode='multiple'
            value={(value as string[] | undefined) ?? []}
            listOptions={getBindingOptions(field.bindings)}
            onChange={(newValue: string[]) => handleValueChange(field.name, newValue)}
            placeholder={field.label}
            disabled={disabled}
            error={error}
            allowClear
          />
        )
      case 'checkboxgroup':
        return (
          <>
            <Label label={field.label} required={field.required} />
            <Checkbox.Group
              className={styles.optionGroup}
              value={(value as string[] | undefined) ?? []}
              options={getBindingOptions(field.bindings)}
              onChange={(newValue) => handleValueChange(field.name, newValue as string[])}
              disabled={disabled}
            />
            <ErrorLabel error={error} />
          </>
        )
      case 'radiogroup':
        return (
          <>
            <Label label={field.label} required={field.required} />
            <Radio.Group
              className={styles.optionGroup}
              value={(value as string | undefined) ?? null}
              options={getBindingOptions(field.bindings)}
              onChange={(event) => handleValueChange(field.name, event.target.value)}
              disabled={disabled}
            />
            <ErrorLabel error={error} />
          </>
        )
      case 'string':
      default:
        return (
          <Input
            label={field.label}
            required={field.required}
            value={(value as string | undefined) ?? ''}
            name={field.name}
            onChange={(event) => handleValueChange(field.name, event.target.value)}
            placeholder={field.label}
            disabled={disabled}
            error={error}
          />
        )
    }
  }

  return (
    <>
      {fields.map((field) => (
        <Col xs={8} key={field.name}>
          {renderControl(field)}
        </Col>
      ))}
    </>
  )
}
